import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoCloseOutline } from 'react-icons/io5';
import { useDarkMode } from '../../../context/DarkModeContext';
import { NotesTab } from './NotesTab';
import { CardsTab } from './CardsTab';

type Tab = 'notes' | 'cards';

interface RetroSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RetroSidePanel: React.FC<RetroSidePanelProps> = ({
  isOpen,
  onClose,
}) => {
  const { darkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState<Tab>('notes');

  // Add keyboard event listener for Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'notes', label: 'Notes' },
    { id: 'cards', label: 'Career Cards' },
  ];

  return (
    <motion.div
      initial={{ x: '-100%' }}
      animate={{ x: isOpen ? 0 : '-100%' }}
      transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
      className={`fixed left-0 top-0 h-full w-80 flex flex-col ${
        darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      } border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'} shadow-lg z-40`}
    >
      {/* Header */}
      <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Content Library</h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700`}
            aria-label="Close panel"
          >
            <IoCloseOutline size={24} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              activeTab === tab.id
                ? `${darkMode ? 'text-blue-400 border-blue-400' : 'text-blue-600 border-blue-600'} border-b-2`
                : `${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {activeTab === 'notes' && <NotesTab />}
        {activeTab === 'cards' && <CardsTab />}
      </div>
    </motion.div>
  );
}; 